import { environment } from 'src/environments/environment'

import { Injectable } from '@angular/core'
import { SafeHtml } from '@angular/platform-browser'
import { RightPanelComponent } from '@billing/right-panel/right-panel.component'
import { FeatureProperty } from '@classes/FeatureProperty'
import { ProjectService } from '@services/project.service'

import { AuthenticationService } from './authentication.service'
import { ConnectionService } from './connection.service'
import { PointerService } from './pointer.service'

export type BuilderTabs = "Scene" | "Feature" | "Details" | "Files" | "Permissions" | "Connection"

@Injectable({
  providedIn: 'root'
})
export class RightSidebarService {
  public attachments: FeatureProperty[] = []
  public offCanvasReference: RightPanelComponent
  public open: boolean = false
  public panelTitle: string
  public right: string = "-360px"
  public sanitizedDisplay: SafeHtml
  public tab: BuilderTabs | string = "Scene"
  public expand: boolean = false

  constructor(
    public authenticationService: AuthenticationService,
    public connectionService: ConnectionService,
    public pointerService: PointerService,
    public projectService: ProjectService,
  ) { }

  openPanel(panelTitle?: string) {
    if (panelTitle) this.tab = panelTitle
    this.right = "0px"
    this.open = true
  }

  closePanel(afterClose?: CallableFunction) {
    this.right = "-360px"
    this.open = false
    if (this.tab == 'Connection') this.connectionService.clearConnectionDataPickerCallback()
    if (afterClose) {
      setTimeout(() => {
        afterClose()
      }, 500) // Matches CSS transition time for closing panel
    }
  }

  togglePanel() {
    if (this.open) this.closePanel()
    else this.openPanel()
  }


  expandPanel() {
    this.expand = !this.expand
  }

  setTab(tab: BuilderTabs) {
    this.tab = tab
  }

  prepareHTML(containerClass: string) {
    setTimeout(() => {
      const [container]: Element[] = document.getElementsByClassName(containerClass) as any
      if (container === undefined) return

      // Ensure embedded media is displayed correctly
      container.querySelectorAll('oembed[url]').forEach(element => {
        if (element.childNodes.length == 0) { // @ts-ignore
          iframely.load(element, element.attributes.url.value)
        }

        element.setAttribute('style', 'width:100%')
      })

      container.querySelectorAll('div[data-oembed-url]').forEach(element => {
        // Discard the static media preview from the database (empty the <div data-oembed-url="...">).
        while (element.firstChild) {
          element.removeChild(element.firstChild)
        }

        // @ts-ignore
        iframely.load(element, element.dataset.oembedUrl) // Generate the media preview using Iframely. 
      })

      // Make all links open a new tab
      container.querySelectorAll('a').forEach(link => link.setAttribute('target', '_blank'))

      // Generate new tokens for images hosted by us
      container.querySelectorAll('img').forEach(img => {
        this.pointerService.setPointerState('clickable', img.style)

        // Insert loading placeholder before the image
        const placeholder = document.createElement('div')
        placeholder.style.cssText = 'display:flex;align-items:center;justify-content:center;gap:8px;padding:24px;margin:8px 0;border:1px dashed #ECEAE5;border-radius:6px;font-size:12px;color:#A8A39A;background:#FAFAF8;'
        placeholder.innerHTML = '<div class="spinner-border spinner-border-sm text-secondary" role="status"></div> <span>Loading image...</span>'
        img.parentNode.insertBefore(placeholder, img)
        img.style.display = 'none'

        const showImage = () => {
          placeholder.remove()
          img.style.display = ''
        }

        img.addEventListener('load', showImage)
        img.addEventListener('error', () => placeholder.remove())

        const url = new URL(img.src)
        const fileRoute = this.getProjectFileRoute(url)

        if (fileRoute != null) {
          if (this.isPublicViewerProject()) {
            url.href = this.getPublicProjectFileUrl(fileRoute)
            img.src = url.href
          } else if (url.searchParams.has('token')) {
            this.authenticationService.getAuthenticatedToken().subscribe({
              next: token => {
                const { fileID, projectID, hash } = fileRoute
                url.href = `${environment.api}/file/token/${fileID}/${projectID}/${hash}?token=${encodeURIComponent(token)}`
                img.src = url.href
              },
              error: () => placeholder.remove()
            })
          }
        }

        // If image is already loaded (cached), show immediately
        if (img.complete && img.naturalWidth > 0) showImage()

        // Adds a click listener to the img tag that opens the image in a new tab
        img.addEventListener('click', () => {
          const url = img.src
          const filename = url.substring(url.lastIndexOf('/') + 1)

          // Create a new window/tab
          const newWindow = window.open('', '_blank')

          // Write content to the new window/tab
          newWindow.document.write(`
            <html>
              <head>
                <title>${filename}</title>
              </head>
              <body>
                <img src="${url}" style='max-width:500px'>
              </body>
            </html>`
          )

          // Close the document to allow interaction
          newWindow.document.close()
        })
      })

    })
  }

  public rewriteHostedProjectImageUrls(html: string) {
    if (!html || !this.isPublicViewerProject()) return html

    const template = document.createElement('template')
    template.innerHTML = html

    template.content.querySelectorAll('img').forEach(img => {
      const source = img.getAttribute('src')
      if (!source) return

      let url: URL

      try {
        url = new URL(source, window.location.origin)
      } catch {
        return
      }

      const fileRoute = this.getProjectFileRoute(url)

      if (fileRoute != null) {
        img.setAttribute('src', this.getPublicProjectFileUrl(fileRoute))
      }
    })

    return template.innerHTML
  }

  private isPublicViewerProject() {
    return this.authenticationService.currentModule == 'viewer'
      && this.projectService.currentProject?.publicStatus
  }

  private getPublicProjectFileUrl(fileRoute: { fileID: string, projectID: number, hash: string }) {
    const { fileID, projectID, hash } = fileRoute

    return `${environment.api}/file/public/${fileID}/${projectID}/${hash}`
  }

  private getProjectFileRoute(url: URL) {
    const pathParts = url.pathname.split('/').filter(part => part !== '')
    const fileIndex = pathParts.lastIndexOf('file')

    if (fileIndex < 0 || pathParts[fileIndex + 1] !== 'project') return null

    const [fileID, projectIDValue, hash] = pathParts.slice(fileIndex + 2, fileIndex + 5)
    const projectID = Number(projectIDValue)

    if (fileID == null || hash == null || Number.isNaN(projectID)) return null

    return { fileID, projectID, hash }
  }

}
